/**
 * Configuration API centralisée pour baoubab
 */

const isDev = import.meta.env.DEV;

// Utiliser l'URL de l'API depuis les variables d'environnement ou une valeur par défaut
// ⚠️ On force désormais l'utilisation de VITE_API_URL même en développement afin d'éviter le proxy local
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.baoubab.space';

/**
 * Configuration axios par défaut
 */
const apiConfig = {
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
};

const PUBLIC_ROUTES = [
  '/public/products',
  '/settings/public',
  '/user/login',
  '/user/verify-otp',
  '/user/refresh-token',
];

const AUTH_NO_RETRY_PATHS = [
  '/user/refresh-token',
  '/user/logout',
  '/user/login',
  '/user/verify-otp',
];

let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const TOKEN_REFRESH_MARGIN_MS = 300000;

const tokenNeedsRefresh = () => {
  const accessToken = localStorage.getItem('access_token');
  const refreshTokenValue = localStorage.getItem('refresh_token');
  if (!refreshTokenValue) {
    return false;
  }
  if (!accessToken) {
    return true;
  }
  const expiresAt = localStorage.getItem('access_token_expires_at');
  if (!expiresAt) {
    return false;
  }
  return parseInt(expiresAt, 10) - Date.now() < TOKEN_REFRESH_MARGIN_MS;
};

const ensureValidAccessToken = async (forceRefresh = false) => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  if (!refreshTokenValue) {
    return localStorage.getItem('access_token');
  }

  if (!forceRefresh && !tokenNeedsRefresh()) {
    return localStorage.getItem('access_token');
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = performTokenRefresh()
    .then((accessToken) => {
      processQueue(null, accessToken);
      return accessToken;
    })
    .catch((error) => {
      processQueue(error, null);
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
      isRefreshing = false;
    });

  return refreshPromise;
};

const clearAuthStorage = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('access_token_expires_at');
  localStorage.removeItem('refresh_token_expires');
  localStorage.removeItem('user');
};

const emitSessionExpired = () => {
  window.dispatchEvent(new CustomEvent('apiError', {
    detail: { status: 401, message: 'Session expirée' },
  }));
  if (!window.location.pathname.includes('/auth')) {
    window.location.href = '/auth';
  }
};

const persistTokens = (tokens) => {
  const { accessToken, refreshToken: newRefreshToken, expiresIn } = tokens;
  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('refresh_token', newRefreshToken);
  if (expiresIn) {
    const expiresAt = Date.now() + (expiresIn * 1000);
    localStorage.setItem('access_token_expires_at', expiresAt.toString());
  }
  return accessToken;
};

const performTokenRefresh = async () => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  if (!refreshTokenValue) {
    throw new Error('No refresh token');
  }

  const axios = await createAxiosInstance();
  const response = await axios.post(
    '/user/refresh-token',
    { refreshToken: refreshTokenValue },
    { _skipAuthRefresh: true }
  );

  if (!response.data?.tokens) {
    throw new Error('Invalid refresh response');
  }

  return persistTokens(response.data.tokens);
};

const requestInterceptor = async (config) => {
  const isPublicRoute = PUBLIC_ROUTES.some((route) => config.url?.includes(route));

  if (!isPublicRoute && !config._skipAuthRefresh) {
    try {
      const token = await ensureValidAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      if (isDev) {
        console.error('Impossible de rafraîchir le token avant la requête:', e);
      }
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  }

  if (isDev) {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
  }
  return config;
};

const responseInterceptor = (response) => response;

const errorInterceptor = async (error) => {
  const message = error.response?.data?.error || error.response?.data?.message || error.message;
  const originalRequest = error.config;

  if (error.response?.status === 401 && originalRequest) {
    const requestUrl = originalRequest.url || '';
    const isPublicRoute = ['/public/products', '/settings/public'].some((route) => requestUrl.includes(route));
    const skipRetry = AUTH_NO_RETRY_PATHS.some((path) => requestUrl.includes(path));

    if (isPublicRoute || skipRetry || originalRequest._retry) {
      return Promise.reject({
        message,
        status: error.response?.status,
        data: error.response?.data,
        originalError: error,
      });
    }

    if (isRefreshing && refreshPromise) {
      return refreshPromise
        .then((accessToken) => {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return createAxiosInstance().then((axios) => axios(originalRequest));
        })
        .catch((refreshError) => {
          clearAuthStorage();
          emitSessionExpired();
          return Promise.reject({
            message: 'Session expirée, veuillez vous reconnecter',
            status: 401,
            originalError: refreshError,
          });
        });
    }

    originalRequest._retry = true;

    try {
      if (!localStorage.getItem('refresh_token')) {
        throw new Error('No refresh token');
      }

      const accessToken = await ensureValidAccessToken(true);
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      const axios = await createAxiosInstance();
      return axios(originalRequest);
    } catch (refreshError) {
      clearAuthStorage();
      emitSessionExpired();
      return Promise.reject({
        message: 'Session expirée, veuillez vous reconnecter',
        status: 401,
        originalError: refreshError,
      });
    }
  }

  return Promise.reject({
    message,
    status: error.response?.status,
    data: error.response?.data,
    originalError: error,
  });
};

/**
 * Instance axios configurée
 */
let axiosInstance;

// Import dynamique d'axios pour éviter les erreurs SSR
const createAxiosInstance = async () => {
  if (!axiosInstance) {
    const axios = (await import('axios')).default;
    axiosInstance = axios.create(apiConfig);

    // Appliquer les intercepteurs
    axiosInstance.interceptors.request.use(requestInterceptor);
    axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor);
  }
  return axiosInstance;
};

/**
 * Méthodes HTTP génériques
 */
export const api = {
  async get(url, config = {}) {
    const axios = await createAxiosInstance();
    return axios.get(url, config);
  },

  async post(url, data = {}, config = {}) {
    const axios = await createAxiosInstance();
    return axios.post(url, data, config);
  },

  async put(url, data = {}, config = {}) {
    const axios = await createAxiosInstance();
    return axios.put(url, data, config);
  },

  async patch(url, data = {}, config = {}) {
    const axios = await createAxiosInstance();
    return axios.patch(url, data, config);
  },

  async delete(url, config = {}) {
    const axios = await createAxiosInstance();
    return axios.delete(url, config);
  }
};

/**
 * Service d'authentification
 */
export const authService = {
  // Envoi OTP de connexion
  login: async (email) => {
    // Backend: prefix /api/user => route /api/user/login
    const response = await api.post('/user/login', { email });
    return response.data;
  },

  // Vérification OTP
  verifyOtp: async (email, otp) => {
    // Backend: /api/user/verify-otp
    const response = await api.post('/user/verify-otp', { email, otp });
    if (response.data.tokens) {
      const { accessToken, refreshToken, expiresIn } = response.data.tokens;
      
      // Stocker les tokens JWT
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      
      // Calculer et stocker la date d'expiration absolue (en ms)
      if (expiresIn) {
        const expiresAt = Date.now() + (expiresIn * 1000);
        localStorage.setItem('access_token_expires_at', expiresAt.toString());
      }
    }
    return response.data;
  },
  
  refreshToken: async () => {
    try {
      const accessToken = await performTokenRefresh();
      return {
        tokens: {
          accessToken,
          refreshToken: localStorage.getItem('refresh_token'),
          expiresIn: null,
        },
      };
    } catch (error) {
      clearAuthStorage();
      throw error;
    }
  },

  getMe: async () => {
    const response = await api.get('/user/me');
    return response.data;
  },

  // Mettre à jour le profil du parent connecté
  updateProfile: async (id, userData) => {
    const response = await api.put(`/user/${id}`, userData);
    return response.data?.data || response.data;
  },
  
  // Nouvelle méthode pour se déconnecter via l'API
  logout: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return;
    
    try {
      // Backend: /api/user/logout
      await api.post('/user/logout', { refreshToken });
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('access_token_expires_at');
      localStorage.removeItem('refresh_token_expires');
    }
  },
  
  // Vérifier si l'utilisateur est authentifié
  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },

};

/**
 * Service des utilisateurs
 */
export const userService = {
  // Récupérer tous les utilisateurs
  getAll: async (page = 0, perPage = 10) => {
    // Backend: prefix /api/user => GET /api/user?page=&perPage=
    const response = await api.get(`/user?page=${page}&perPage=${perPage}`);
    return response.data;
  },

  // Récupérer un utilisateur par ID
  getById: async (id) => {
    // Backend: GET /api/user/:id
    const response = await api.get(`/user/${id}`);
    return response.data;
  },

  // Créer un utilisateur
  create: async (userData) => {
    // Backend: POST /api/user
    const response = await api.post('/user', userData);
    return response.data;
  },

  // Mettre à jour un utilisateur
  update: async (id, userData) => {
    // Backend: PUT /api/user/:id
    const response = await api.put(`/user/${id}`, userData);
    return response.data?.data || response.data;
  },

  // Supprimer un utilisateur
  delete: async (id) => {
    // Backend: DELETE /api/user/:id
    const response = await api.delete(`/user/${id}`);
    return response.data;
  }
};

/**
 * Service des enfants (Kids)
 */
export const kidService = {
  // Récupérer tous les enfants
  getAll: async () => {
    const response = await api.get('/kids');
    return response.data;
  },

  // Récupérer un enfant par ID
  getById: async (id) => {
    const response = await api.get(`/kids/${id}`);
    return response.data;
  },

  // Créer un enfant
  create: async (kidData) => {
    const response = await api.post('/kids', kidData);
    return response.data;
  },

  // Mettre à jour un enfant
  update: async (id, kidData) => {
    const response = await api.put(`/kids/${id}`, kidData);
    return response.data;
  },

  // Supprimer un enfant
  delete: async (id) => {
    const response = await api.delete(`/kids/${id}`);
    return response.data;
  }
};

/**
 * Service des questions
 */
export const questionService = {
  // Récupérer toutes les questions
  getAll: async () => {
    const response = await api.get('/questions');
    return response.data;
  },

  // Récupérer les questions par tranche d'âge
  getByAgeSection: async (ageSectionId) => {
    const response = await api.get(`/questions/age-section/${ageSectionId}`);
    return response.data;
  },

  // Récupérer une question par ID
  getById: async (id) => {
    const response = await api.get(`/questions/${id}`);
    return response.data;
  },

  // Créer une question
  create: async (questionData) => {
    const response = await api.post('/questions', questionData);
    return response.data;
  },

  // Mettre à jour une question
  update: async (id, questionData) => {
    const response = await api.put(`/questions/${id}`, questionData);
    return response.data;
  },

  // Supprimer une question
  delete: async (id) => {
    const response = await api.delete(`/questions/${id}`);
    return response.data;
  }
};

/**
 * Service des réponses
 */
export const answerService = {
  // Récupérer toutes les réponses
  getAll: async () => {
    const response = await api.get('/answers');
    return response.data;
  },

  // Récupérer une réponse par ID
  getById: async (id) => {
    const response = await api.get(`/answers/${id}`);
    return response.data;
  },

  // Créer une réponse
  create: async (answerData) => {
    const response = await api.post('/answers', answerData);
    return response.data;
  },

  // Mettre à jour une réponse
  update: async (id, answerData) => {
    const response = await api.put(`/answers/${id}`, answerData);
    return response.data;
  },

  // Supprimer une réponse
  delete: async (id) => {
    const response = await api.delete(`/answers/${id}`);
    return response.data;
  }
};

/**
 * Service des types de profil
 */
export const profilTypeService = {
  // Récupérer tous les types de profil
  getAll: async () => {
    const response = await api.get('/profil_type');
    return response.data;
  },

  // Récupérer un type de profil par ID
  getById: async (id) => {
    const response = await api.get(`/profil_type/${id}`);
    return response.data;
  },

  // Créer un type de profil
  create: async (profilTypeData) => {
    const response = await api.post('/profil_type', profilTypeData);
    return response.data;
  },

  // Mettre à jour un type de profil
  update: async (id, profilTypeData) => {
    const response = await api.put(`/profil_type/${id}`, profilTypeData);
    return response.data;
  },

  // Supprimer un type de profil
  delete: async (id) => {
    const response = await api.delete(`/profil_type/${id}`);
    return response.data;
  }
};

/**
 * Service des avatars
 */
export const avatarService = {
  // Récupérer tous les avatars
  getAll: async () => {
    const response = await api.get('/avatars');
    return response.data;
  },

  // Récupérer un avatar par ID
  getById: async (id) => {
    const response = await api.get(`/avatars/${id}`);
    return response.data;
  },

  // Créer un avatar
  create: async (avatarData) => {
    const response = await api.post('/avatars', avatarData);
    return response.data;
  },

  // Mettre à jour un avatar
  update: async (id, avatarData) => {
    const response = await api.put(`/avatars/${id}`, avatarData);
    return response.data;
  },

  // Supprimer un avatar
  delete: async (id) => {
    const response = await api.delete(`/avatars/${id}`);
    return response.data;
  }
};

/**
 * Service des tranches d'âge
 */
export const ageSectionService = {
  // Récupérer toutes les tranches d'âge
  getAll: async () => {
    const response = await api.get('/ages_sections');
    return response.data;
  },

  // Récupérer une tranche d'âge par ID
  getById: async (id) => {
    const response = await api.get(`/ages_sections/${id}`);
    return response.data;
  },

  // Créer une tranche d'âge
  create: async (ageSectionData) => {
    const response = await api.post('/ages_sections', ageSectionData);
    return response.data;
  },

  // Mettre à jour une tranche d'âge
  update: async (id, ageSectionData) => {
    const response = await api.put(`/ages_sections/${id}`, ageSectionData);
    return response.data;
  },

  // Supprimer une tranche d'âge
  delete: async (id) => {
    const response = await api.delete(`/ages_sections/${id}`);
    return response.data;
  }
};

/**
 * Service de profilage
 */
export const profilingService = {
  // Analyser le profil d'un enfant
  analyze: async (kidId, answers, ageSectionId) => {
    const response = await api.post('/profiling/analyze', {
      kid_id: kidId,
      answers,
      age_section_id: ageSectionId
    });
    return response.data;
  },

  // Envoyer les résultats par email
  sendResults: async (kidId) => {
    const response = await api.post('/profiling/send-results', {
      kid_id: kidId,
    });
    return response.data;
  },
};

/**
 * Service de test
 */
export const testService = {
  // Test de connexion
  testConnection: async () => {
    const response = await api.get('/test');
    return response.data;
  },

  // Envoyer un email de test
  sendTestEmail: async (to, subject, html) => {
    const response = await api.post('/test/send-test-email', {
      to,
      subject,
      html
    });
    return response.data;
  }
};

/**
 * Services pour la gestion des vidéos
 */
export const videoService = {
  // Récupérer toutes les vidéos actives (avec authentification utilisateur)
  getActiveVideos: async () => {
    // Vérifier que l'utilisateur est connecté
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Vous devez être connecté pour accéder aux vidéos');
    }
    
    const response = await api.get('/public/videos/active');
    return response.data;
  },

  // Récupérer les vidéos par profil et tranche d'âge (endpoint principal)
  getVideosByProfilAndAge: async (profilTypeId, agesSectionId) => {
    // Vérifier que l'utilisateur est connecté
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Vous devez être connecté pour accéder aux vidéos');
    }
    
    const response = await api.get(`/public/videos/profil/${profilTypeId}/age/${agesSectionId}`);
    return response.data;
  },

  // Récupérer une vidéo par ID (pour VideoDetails)
  getVideoById: async (id) => {
    // Vérifier que l'utilisateur est connecté
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Vous devez être connecté pour accéder aux vidéos');
    }
    
    const response = await api.get(`/public/videos/${id}`);
    return response.data;
  }
};

/**
 * Utilitaires pour la gestion des erreurs
 */
export const handleApiError = (error) => {
  if (error.status === 400) {
    return 'Données invalides. Veuillez vérifier vos informations.';
  }
  if (error.status === 404) {
    return 'Ressource non trouvée.';
  }
  if (error.status === 409) {
    return 'Conflit de données. Cette ressource existe déjà.';
  }
  if (error.status === 500) {
    return 'Erreur serveur. Veuillez réessayer plus tard.';
  }
  if (!error.status) {
    return 'Erreur de connexion. Vérifiez votre connexion internet.';
  }
  return error.message || 'Une erreur inattendue s\'est produite.';
};

export default api;
